import clsx from 'clsx';
import * as React from 'react';
import { FocusEventHandler, FormEvent } from 'react';
import NumberFormat, { NumberFormatProps } from 'react-number-format';

export interface PhoneInputProps extends Omit<NumberFormatProps, 'defaultValue'> {
  inputRef?: (ref: HTMLElement | null) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  value?: string;
  readonly?: boolean;
  shortMask?: boolean;
  onChange?: (event: FormEvent<HTMLInputElement>) => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onFocus,
  inputRef,
  readonly,
  shortMask,
  onChange,
  className,
  ...other
}) => {
  const startSymbols = shortMask ? '7' : '79';
  const defaultValue = `${startSymbols}${shortMask ? value : value?.substr(1)}`;
  const placeholder = shortMask ? '+7 (___) ___-__-__' : '+7 (9__) ___-__-__';
  return (
    <NumberFormat
      {...other}
      className={clsx(className, 'ym-hide-content')}
      onFocus={(event): void => {
        onFocus?.(event);
        const onlyNumbers = (event.target.value || '').replace(/[^\d]/g, '');
        if (onlyNumbers.length < startSymbols.length) {
          setTimeout(() => {
            // eslint-disable-next-line no-param-reassign
            event.target.value = placeholder;
            event.target.select();
          });
        } else {
          setTimeout(() => event.target.select(), 0);
        }
      }}
      onChange={(event): void => {
        const onlyNumbers = (event.target.value || '').replace(/[^\d]/g, '');
        const minSymbolsAfterMask =
          onlyNumbers.length < startSymbols.length + startSymbols.length ? onlyNumbers.length : startSymbols.length;
        const minCaretPosition = 3 + minSymbolsAfterMask;
        if (
          // prettier-ignore
          // prettier: FIXME: https://github.com/prettier/prettier/pull/7111
          event.target.selectionStart
          && event.target.selectionStart < minCaretPosition
        ) {
          const { target } = event;
          setTimeout(() => target.setSelectionRange(minCaretPosition, minCaretPosition));
        }
        onChange?.(event);
      }}
      getInputRef={inputRef}
      value={defaultValue || undefined}
      inputMode="tel"
      removeFormatting={(maskedPhone): string => {
        const isFormatted = maskedPhone.startsWith('+');
        const onlyNumbers = maskedPhone.replace(/[^\d]/g, '');
        const numberWithReplacedCountryCode =
          onlyNumbers.substr(0, 1) === '8' && onlyNumbers.length === 11 ? `7${onlyNumbers.substr(1)}` : onlyNumbers;
        const isStartedCorrect = numberWithReplacedCountryCode.substr(0, startSymbols.length) === startSymbols;
        let newValue: string;
        if (isFormatted) {
          if (isStartedCorrect) {
            newValue = numberWithReplacedCountryCode;
          } else if (numberWithReplacedCountryCode.length < startSymbols.length) {
            // backspace
            newValue = startSymbols;
          } else if (startSymbols.indexOf(numberWithReplacedCountryCode.substr(0, 1)) !== -1) {
            newValue = `${startSymbols}${numberWithReplacedCountryCode.substr(1)}`;
          } else {
            newValue = `${startSymbols}${numberWithReplacedCountryCode}`;
          }
        } else if (numberWithReplacedCountryCode.length === 10 && !isStartedCorrect) {
          newValue = `7${numberWithReplacedCountryCode}`;
        } else {
          newValue = isStartedCorrect
            ? numberWithReplacedCountryCode
            : (newValue = `${startSymbols}${numberWithReplacedCountryCode}`);
        }
        return newValue;
      }}
      format={(maskedPhone): string => {
        const newValue = maskedPhone;
        const v = (idx: number): string => newValue[idx] || '_';
        return `+${v(0)} (${v(1)}${v(2)}${v(3)}) ${v(4)}${v(5)}${v(6)}-${v(7)}${v(8)}-${v(9)}${v(10)}`;
      }}
      displayType={readonly ? 'text' : 'input'}
      mask="_"
      placeholder={placeholder}
    />
  );
};

export default PhoneInput;
